<script setup>
/**
 * 新增 / 编辑一把 key 的表单弹窗。
 *
 * **明文不回显**:接口只返回脱敏值,想回显也无从回显。编辑时密钥字段是空的(placeholder 说
 * 「不填则不换」)—— 填了就是替换成一把新密钥,不填就是只改配置列。
 *
 * base_url 留空 = 继承厂商的那个 —— 厂商的值直接摆进 placeholder,不写一句「不填则继承」。
 *
 * 校验规则和提交体的转换在 `../llm-key-form.js`(纯逻辑,可单测),与 `llm_key` 表上的 CHECK 约束一致。
 *
 * 提交失败时:**弹窗不关、字段不清**,错误原文显示在按钮上方,再点一次保存就是重试。
 */
import { computed, ref, watch } from 'vue'
import { Button, FormField, Input, Modal, SegmentedControl, useToast } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { emptyKeyForm, keyFieldsOf, keyFormFromRow, toKeySaveBody, validateKeyForm } from '../llm-key-form.js'

const props = defineProps({
  open: { type: Boolean, default: false },
  /** key 池命名空间 `<能力>:<接入代码>`：露出哪些字段、字段名与说明都按它定（llm-key-form.js 的 keyFieldsOf）。 */
  vendor: { type: String, required: true },
  /** 厂商的 base_url:这把 key 不填时继承的就是它,所以它是 placeholder。 */
  providerBaseUrl: { type: String, default: null },
  /** @type {import('vue').PropType<import('../../../api/llm/index.js').KeyRow|null>} null = 新增。 */
  keyRow: { type: Object, default: null },
  /** 保存一把 key：能力接入池与服务凭据池走各自的接口。 */
  saveKey: { type: Function, required: true },
})

const emit = defineEmits(['update:open', 'saved'])

const { t, te } = useI18n()
const toast = useToast()

const form = ref(emptyKeyForm())
const saving = ref(false)
const submitError = ref('')
/** 提交过一次之后才显示逐字段的错误 —— 刚打开就满屏红字是在骂人。 */
const submitted = ref(false)

// 每次打开都从当前行重建表单:上一次编辑的残留值不该跟到下一把 key 上。
watch(
  () => [props.open, props.keyRow],
  ([open]) => {
    if (!open) return
    form.value = props.keyRow ? keyFormFromRow(props.keyRow) : emptyKeyForm()
    submitError.value = ''
    submitted.value = false
  },
  { immediate: true },
)

const isEdit = computed(() => props.keyRow !== null)

const enabledOptions = computed(() => [
  { value: 'enabled', label: t('visualGameAdmin.llm.providers.status.enabled') },
  { value: 'disabled', label: t('visualGameAdmin.llm.providers.status.disabled') },
])

const enabledValue = computed({
  get: () => (form.value.enabled ? 'enabled' : 'disabled'),
  set: (value) => {
    form.value.enabled = value === 'enabled'
  },
})

const fields = computed(() => keyFieldsOf(props.vendor))
const capability = computed(() => props.vendor.slice(0, props.vendor.indexOf(':')))
const fieldErrors = computed(() => validateKeyForm(form.value, fields.value))

/**
 * 字段名与说明：服务凭据（avatar、rtc）有按 vendor 的写法（如 ARTC AppId），其余用通用文案；没有通用文案的说明返回空串。
 * @param {string} key
 * @param {string|null} fallback
 */
function fieldText(key, fallback) {
  const vendorKey = `visualGameAdmin.llm.providers.form.vendorFields.${capability.value}.${key}`
  if (te(vendorKey)) return t(vendorKey)
  return fallback === null ? '' : t(`visualGameAdmin.llm.providers.form.${fallback}`)
}

/** @param {string} field */
function errorOf(field) {
  if (!submitted.value) return ''
  const code = fieldErrors.value[field]
  return code ? t(`visualGameAdmin.llm.providers.errors.${code}`) : ''
}

async function submit() {
  submitted.value = true
  if (Object.keys(fieldErrors.value).length > 0) return
  saving.value = true
  submitError.value = ''
  try {
    await props.saveKey(toKeySaveBody(form.value, fields.value))
    emit('saved')
  } catch (err) {
    submitError.value = err.message
    toast.error(t('visualGameAdmin.llm.providers.toast.failed'), { duration: 0 })
  } finally {
    saving.value = false
  }
}
</script>

<template>
  <Modal
    :open="open"
    :title="t(`visualGameAdmin.llm.providers.form.${isEdit ? 'editTitle' : 'createTitle'}`)"
    @update:open="emit('update:open', $event)"
  >
    <form
      class="key-form"
      data-insp-path="apps/visual-game-admin/src/pages/llm/provider/KeyFormModal.vue#key-form"
      @submit.prevent="submit"
    >
      <FormField
        class="key-form__wide"
        :label="fieldText('apiKey', 'apiKey')"
        :error="errorOf('api_key')"
        :required="!isEdit"
      >
        <Input
          v-model="form.api_key"
          type="password"
          autocomplete="off"
          :placeholder="isEdit ? t('visualGameAdmin.llm.providers.form.keepSecret') : ''"
          :invalid="Boolean(errorOf('api_key'))"
        />
      </FormField>

      <!-- 复合凭据的账号侧：tts（豆包 appId）可选、rtc（ARTC AppId）必填；其余能力不渲染。 -->
      <FormField
        v-if="fields.account_id !== 'hidden'"
        class="key-form__wide"
        :label="fieldText('accountId', 'accountId')"
        :error="errorOf('account_id')"
        :required="fields.account_id === 'required'"
      >
        <Input
          v-model="form.account_id"
          autocomplete="off"
          :placeholder="fieldText('accountIdHint', 'accountIdHint')"
          :invalid="Boolean(errorOf('account_id'))"
        />
      </FormField>

      <FormField
        class="key-form__wide"
        :label="t('visualGameAdmin.llm.providers.form.label')"
        :error="errorOf('label')"
        required
      >
        <Input
          v-model="form.label"
          :invalid="Boolean(errorOf('label'))"
        />
      </FormField>

      <!-- 每 key 的 Base URL 覆盖只在 text 网关（llm_key.base_url > 能力接入 > 适配器默认）与 Vidu 数字人（覆盖主机）生效；
           媒体 provider 的接入点写死在包内，展示这个字段只会误导。 -->
      <FormField
        v-if="fields.base_url !== 'hidden'"
        class="key-form__wide"
        :label="fieldText('baseUrl', 'baseUrl')"
        :hint="fieldText('baseUrlHint', null)"
        :error="errorOf('base_url')"
      >
        <Input
          v-model="form.base_url"
          type="url"
          :placeholder="providerBaseUrl ?? ''"
          :invalid="Boolean(errorOf('base_url'))"
        />
      </FormField>

      <FormField
        :label="t('visualGameAdmin.llm.providers.form.weight')"
        :error="errorOf('weight')"
        required
      >
        <Input
          v-model="form.weight"
          type="number"
          :invalid="Boolean(errorOf('weight'))"
        />
      </FormField>

      <FormField
        :label="t('visualGameAdmin.llm.providers.form.concurrency')"
        :error="errorOf('max_concurrency')"
        required
      >
        <Input
          v-model="form.max_concurrency"
          type="number"
          :invalid="Boolean(errorOf('max_concurrency'))"
        />
      </FormField>

      <!-- 媒体与服务调用不产生 token 用量，TPM 上限永不触发——非 text 池不给配。 -->
      <FormField
        v-if="fields.max_tpm"
        :label="t('visualGameAdmin.llm.providers.form.tpm')"
        :error="errorOf('max_tpm')"
      >
        <Input
          v-model="form.max_tpm"
          type="number"
          :placeholder="t('visualGameAdmin.llm.providers.keys.unlimited')"
          :invalid="Boolean(errorOf('max_tpm'))"
        />
      </FormField>

      <FormField
        :label="t('visualGameAdmin.llm.providers.form.rpm')"
        :error="errorOf('max_rpm')"
      >
        <Input
          v-model="form.max_rpm"
          type="number"
          :placeholder="t('visualGameAdmin.llm.providers.keys.unlimited')"
          :invalid="Boolean(errorOf('max_rpm'))"
        />
      </FormField>

      <FormField
        :label="t('visualGameAdmin.llm.providers.form.priority')"
        :error="errorOf('priority')"
        required
      >
        <Input
          v-model="form.priority"
          type="number"
          :invalid="Boolean(errorOf('priority'))"
        />
      </FormField>

      <FormField :label="t('visualGameAdmin.llm.providers.form.enabled')">
        <SegmentedControl
          v-model="enabledValue"
          :options="enabledOptions"
        />
      </FormField>

      <p
        v-if="submitError"
        class="vga-error key-form__error"
        role="alert"
      >
        {{ submitError }}
      </p>
    </form>

    <template #footer>
      <Button
        variant="ghost"
        @click="emit('update:open', false)"
      >
        {{ t('visualGameAdmin.common.cancel') }}
      </Button>
      <Button
        variant="primary"
        :loading="saving"
        @click="submit"
      >
        {{ t('visualGameAdmin.llm.providers.form.submit') }}
      </Button>
    </template>
  </Modal>
</template>

<style scoped>
.key-form {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: start;
  gap: var(--drama-space-lg);
  min-width: min(560px, 76vw);
}

.key-form__wide,
.key-form__error {
  grid-column: 1 / -1;
}

.key-form__error {
  margin: 0;
  padding: var(--drama-space-md);
  border: 1px solid var(--drama-color-danger);
  border-radius: var(--drama-radius-sm);
  background: color-mix(in srgb, var(--drama-color-danger) 10%, transparent);
  line-height: var(--drama-line-height-normal);
}
</style>
